import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Fab,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchById, handleError } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from "../../providers/SnackBarProvider";
import axios from "axios";
import { useConfig } from '../../providers/ConfigProvider';
import BottomSheet from '../../layouts/BottomSheet';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2Icon from '@mui/icons-material/Inventory2';

function ViewProduct() {
  const location = useLocation();
  const navigate = useNavigate();
  const [id, setId] = useState(location.state.id);
  const [product, setProduct] = useState(null);
  const {updateTitle} = useAppBar();
  const [isLoading, setIsLoading] = useState(true);
  const snackbar = useSnackbar();
  const [productUpdated, setProductUpdated] = useState(false);
  const [isAssocLoading, setIsAssocLoading] = useState(false);
  const {config, isLoadingConfig} = useConfig();
  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });
  const { t } = useTranslation();

  useEffect(() => { 
    setIsLoading(true);
    const fetchProduct_ = async () => {
      const product = await fetchById(id, 'products');
      setProduct(product);
      setIsLoading(false);
    };
    fetchProduct_();
  }, [productUpdated]);

  // Actualiza el título cuando el producto cambia
  useEffect(() => {
    if (product) {
      updateTitle(product.thumb_url || '', product.name, '', false, Inventory2Icon, handleAvatarClick);
    }
  }, [product]);

  const handleAvatarClick = () => {
    if (product?.photo_url) {
      window.open(product.photo_url, '_blank');
    }
  };

  const handleEdit = () => {
    if (product.is_service === 1) {
      navigate('/main/edit-service', { state: { updateService: product } });
    } else {
      navigate('/main/edit-product', { state: { updateProduct: product } });
    }
  };

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      await axios.delete(
        `${import.meta.env.VITE_API_URL}products?id=${product.id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    setIsAssocLoading(true);
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}products?restore&id=${id}`,
        { withCredentials: true }
      );
      setIsAssocLoading(true);
    } catch (error) {
      handleError(error);
      snackbar(t('common.errorSaving'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleAssoc = async () => {
    setIsAssocLoading(true);
    const products = {"products": [id]}
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}products?associates`,
        products,
        { withCredentials: true }
      );
      setProductUpdated(true);
    } catch (error) {
      handleError(error);
      snackbar(t('common.errorSaving'));
    } finally {
      setIsAssocLoading(false);
    }
  };
  
  if (isLoading || isLoadingConfig) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  return (
    //style={{ overflow: 'auto', maxHeight: '100vh' }}
    <Container maxWidth="xl">

      {/* Info Section */}
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.name')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {product.name_es}
        </Typography>
      </Box>

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.unitLabel')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {product.unit.name}
        </Typography>
      </Box>
      
      {product.complex_unit_quantity && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('view.complexUnitQuantityLabel')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.complex_unit_quantity}
          </Typography>
        </Box>
      )}

      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('view.categoriesLabel')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {product.categories_es}
        </Typography>
      </Box>

      {product.bar_code && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('view.barCodeLabel')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.bar_code}
          </Typography>
        </Box>
      )}

      {product.qr_code && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('view.qrCodeLabel')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.qr_code}
          </Typography>
        </Box>
      )}

      {product.expires === 1 && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('edit.expires')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {t('common.yes')}
          </Typography>
        </Box>
      )}

      {product.notes && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.notes')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.notes}
          </Typography>
        </Box>
      )}

      {product.is_service === 0 && product.quantity_string && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.quantity')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.quantity_string}
          </Typography>

          {product.is_service === 0 && product.quantity_detail_es && (
            <Typography
              variant="body1"
              style={{ fontSize: '16px' }}
            >
              {product.quantity_detail_es}
            </Typography>
          )}

          {product.is_service === 0 && product.quantity_detail_branchs_es && (
            <>
              <Typography variant="caption">{t('view.quantityInBranches')}</Typography>
              <Typography
                variant="body1"
                style={{ fontSize: '16px' }}
              >
                {product.quantity_detail_branchs_es}
              </Typography>
            </>
          )}

        </Box>
      )}

      {product.is_service === 0 && product.min_quantity && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('view.minQuantityLabel')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.min_quantity.toFixed(2)}
          </Typography>
        </Box>
      )}

      {product.cost && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.cost')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.cost.toFixed(2)}
          </Typography>
        </Box>
      )}

      {product.tax && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.tax')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.tax.toFixed(2)}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {product.p1 && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('edit.price1')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.p1.toFixed(2)}
          </Typography>
        </Box>
      )}
      {product.p2 && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('edit.price2')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.p2.toFixed(2)}
          </Typography>
        </Box>
      )}
      {product.p3 && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('edit.price3')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.p3.toFixed(2)}
          </Typography>
        </Box>
      )}

      {(product.p1 || product.p2 || product.p3) && (
        <Divider sx={{ my: 2 }} />
      )}

      {product.is_service === 1 && product.service_duration && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('view.serviceDuration')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {product.service_duration}
          </Typography>
        </Box>
      )}
      {product.is_service === 1 && product.is_study === 1 && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('edit.isStudy')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {t('common.yes')}
          </Typography>
        </Box>
      )}

      {product.deleted === 1 && (
        <Button
          variant="contained"
          onClick={handleRestore}
          fullWidth
          disabled={isAssocLoading}
        >
          {isAssocLoading ? <CircularProgress size={24} /> : t('view.restoreProduct')}
        </Button>
      )}

      {product.is_associate_with_vet === 0 && (
        <Button
          variant="contained"
          onClick={handleAssoc}
          fullWidth
          disabled={isAssocLoading}
        >
          {isAssocLoading ? <CircularProgress size={24} /> : t('view.associateProduct')}
        </Button>
      )}

      {product.is_associate_with_vet === 1 && (
        <BottomSheet 
          zIndex={1001}
          buttonGroups={[
            {
              label: t('common.product'),
              buttons: [
                ...(config.permissions.actions_permissions.products.update ? [{
                  label: t('view.vetActionEdit'),
                  action: handleEdit,
                  icon: <EditIcon />
                }] : []),
                ...(config.permissions.actions_permissions.products.delete ? [{
                  label: t('common.delete'),
                  action: handleDeleteOpen,
                  icon: <DeleteIcon />
                }] : []),
              ].filter(Boolean)
            },
          ]}
        />
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.deleteProductTitle', { name: product?.name_es })}</DialogTitle>
        <DialogContent>
          {t('view.deleteProductMessage', { name: product?.name_es })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
}

export default ViewProduct;